/**
 * petPerfusionTools.js — три количественных окна: «ПЭТ: SUV», «Перфузия КТ
 * (инсульт)», «ДКУ-МРТ: Ktrans».
 *
 * Здесь ТОЛЬКО интерфейс: весь счёт живёт в чистых модулях, которые
 * проверяются без браузера — core/petSuv.js, core/perfusionCT.js,
 * core/dceKtrans.js. Повод — слова владельца: «ПЭТ… PT открывается как
 * картинка»; перфузии инсульта и Ktrans не было вовсе.
 *
 * ЧТО ЭТИ ОКНА ГОВОРЯТ О СЕБЕ САМИ (и это написано в каждом):
 *   · расчёты исследовательские, НЕ для постановки диагноза и выбора лечения;
 *   · SUV показывает выкладку фактора из тегов — врач видит дозу, вес,
 *     времена и остаток распада, а не число из воздуха; недостающий тег —
 *     отказ с именем тега, а не тихая единица;
 *   · CBF в перфузии ЗАНИЖЕН самим методом (SVD-семейство, промерено тестом
 *     до 60–70% истины) — стороны сравнивать можно, абсолютным числам без
 *     калибровки верить нельзя; об этом сказано прямо в окне;
 *   · Патлак в ДКУ честен только при малом вымывании — главный счёт Мурасэ.
 */

import { факторSUVbw, статистикаSUV, mtvTlg } from '../core/petSuv.js';
import { картыПерфузии, кандидатыАИФ } from '../core/perfusionCT.js';
import { паркерAIF, концентрацияИзSPGR, картыКтранс } from '../core/dceKtrans.js';
import { окно, строкаВвода, нарисовать } from './quantTools.js';
import { getSeriesImageIds, getAllSeries, getDICOMTags, getCachedDataSet } from '../loaders/loaderManager.js';
import { томМаски } from '../core/organView.js';
import { showToast } from '../utils/helpers.js';
import { log } from '../utils/logger.js';

const cs = () => window.cornerstone || null;
const $ = (id) => document.getElementById(id);

const ОГОВОРКА = '<div style="margin-top:10px;padding:8px 10px;border:1px solid #5a4a1e;border-radius:8px;'
  + 'background:#241d0c;color:#e8d48b;">Исследовательский расчёт: не для постановки диагноза и не для выбора лечения. '
  + 'Числа зависят от протокола съёмки и тегов файла — сверяйте с заключением рабочей станции.</div>';

// ══════════════════════════════════════════════════════════════
//  Общие мелочи
// ══════════════════════════════════════════════════════════════

/** Том серии из кеша cornerstone: скаляры, размеры, шаг. */
function томСерии(seriesUID) {
  try {
    const кэш = cs()?.cache;
    const все = кэш?.getVolumes?.() || [];
    const хвост = `volume_${seriesUID}`;
    const том = все.find((т) => String(т?.volumeId || '').includes(хвост));
    if (!том) return { ошибка: 'серия не открыта томом — откройте её в окне просмотра (раскладка MPR)' };
    const скаляры = том.voxelManager?.getCompleteScalarDataArray?.() || том.getScalarData?.();
    if (!скаляры?.length) return { ошибка: 'у тома нет данных в памяти' };
    return { том, скаляры, dims: том.dimensions, spacing: том.spacing };
  } catch (e) { return { ошибка: e.message }; }
}

/** Маска активной сегментации, если она нарисована НА ЭТОМ томе. */
function маскаДляТома(dims) {
  const м = томМаски();
  if (м.ошибка) return { маска: null, слов: 'маски нет — ' + м.ошибка };
  const скаляры = м.том?.voxelManager?.getCompleteScalarDataArray?.() || м.том?.getScalarData?.();
  const д = м.том?.dimensions;
  if (!скаляры || !д) return { маска: null, слов: 'у маски нет данных' };
  if (д[0] !== dims[0] || д[1] !== dims[1] || д[2] !== dims[2]) {
    return { маска: null, слов: `маска нарисована на другой серии (${д.join('×')} против ${dims.join('×')})` };
  }
  let занято = 0;
  for (let i = 0; i < скаляры.length; i++) if (скаляры[i]) занято++;
  if (!занято) return { маска: null, слов: 'маска пуста' };
  return { маска: скаляры, слов: `маска: ${занято.toLocaleString('ru-RU')} вокс.` };
}

const строкаЧисла = (подпись, значение) =>
  `<div style="display:flex;justify-content:space-between;gap:10px;margin:3px 0;">`
  + `<span style="color:#9fb3cf;">${подпись}</span><b>${значение}</b></div>`;

/** Кадры динамики: группировка по положению среза, времена из AcquisitionTime. */
async function динамикаСерии(seriesUID, { минВремён = 8, потолокКадров = 2400 } = {}) {
  const imageIds = getSeriesImageIds?.(seriesUID) || [];
  if (!imageIds.length) return { ошибка: 'в серии нет кадров' };
  const попозиции = new Map();
  let просмотрено = 0;
  for (const id of imageIds) {
    if (++просмотрено > потолокКадров) break;
    let теги = {};
    try { теги = await getDICOMTags(id); } catch (e) { continue; }
    const поз = String(теги?.['x00200032']?.value || 'без-позиции');
    const времяСтр = String(теги?.['x00080032']?.value || теги?.['x00080033']?.value || '');
    const м = времяСтр.match(/^(\d{2})(\d{2})(\d{2}(?:\.\d+)?)/);
    if (!м) continue;
    const t = Number(м[1]) * 3600 + Number(м[2]) * 60 + Number(м[3]);
    if (!попозиции.has(поз)) попозиции.set(поз, []);
    попозиции.get(поз).push({ id, t });
  }
  let лучшая = [];
  for (const г of попозиции.values()) if (new Set(г.map((к) => к.t)).size > new Set(лучшая.map((к) => к.t)).size) лучшая = г;
  const времён = new Set(лучшая.map((к) => к.t)).size;
  if (времён < минВремён) {
    return { ошибка: `на одном срезе найдено ${времён} моментов времени — динамика считается от ${минВремён}` };
  }
  лучшая.sort((a, b) => a.t - b.t);
  const кадры = [], времена = [];
  let ширина = 0, высота = 0;
  for (const к of лучшая) {
    try {
      const image = await cs().imageLoader.loadAndCacheImage(к.id);
      const пиксели = image.getPixelData();
      const наклон = Number.isFinite(image.slope) ? image.slope : 1;
      const сдвиг = Number.isFinite(image.intercept) ? image.intercept : 0;
      const данные = new Float32Array(пиксели.length);
      for (let i = 0; i < пиксели.length; i++) данные[i] = пиксели[i] * наклон + сдвиг;
      кадры.push(данные); времена.push(к.t - лучшая[0].t);
      ширина = image.columns; высота = image.rows;
    } catch (e) { log.warn('[Динамика] кадр не загрузился:', e.message); }
  }
  if (кадры.length < минВремён) return { ошибка: 'пиксели кадров динамики не загрузились' };
  return { кадры, времена, ширина, высота, срезов: попозиции.size, кадровВсего: imageIds.length, обрезано: просмотрено > потолокКадров };
}

/** Канвас, по которому можно щёлкнуть — щелчок заполняет поля x/y. */
function щёлкающийКанвас(холст, ширина, высота, наЩелчок) {
  холст.style.cursor = 'crosshair';
  холст.addEventListener('click', (e) => {
    const р = холст.getBoundingClientRect();
    const x = Math.round(((e.clientX - р.left) / р.width) * (ширина - 1));
    const y = Math.round(((e.clientY - р.top) / р.height) * (высота - 1));
    наЩелчок(Math.max(0, Math.min(ширина - 1, x)), Math.max(0, Math.min(высота - 1, y)));
  });
}

// ══════════════════════════════════════════════════════════════
//  1. ПЭТ: SUV
// ══════════════════════════════════════════════════════════════

async function пэтТеги(imageId) {
  let т = {};
  try { т = await getDICOMTags(imageId); } catch (e) { log.debug('[SUV] теги:', e.message); }
  const из = (адр) => т?.[адр]?.value;
  let дозаБк, полупериодС, времяВведения = null, датаВремяВведения = null;
  let philipsSuv, philipsБкМл;
  try {
    const ds = getCachedDataSet?.(imageId);
    const rp = ds?.elements?.x00540016?.items?.[0]?.dataSet;
    if (rp) {
      дозаБк = Number(rp.string('x00181074'));
      полупериодС = Number(rp.string('x00181075'));
      времяВведения = rp.string('x00181072') || null;
      датаВремяВведения = rp.string('x00181078') || null;
    }
    if (ds) {
      const ф = Number(ds.string('x70531000')); if (Number.isFinite(ф)) philipsSuv = ф;
      const б = Number(ds.string('x70531009')); if (Number.isFinite(б)) philipsБкМл = б;
    }
  } catch (e) { log.debug('[SUV] последовательность радиофармпрепарата:', e.message); }
  return {
    units: из('x00541001'), decayCorrection: из('x00541102'),
    дозаБк, полупериодС, времяВведения, датаВремяВведения,
    датаСерии: из('x00080021'), времяСерии: из('x00080031'),
    датаСъёмки: из('x00080022'), времяСъёмки: из('x00080032'),
    весКг: Number(из('x00101030')),
    philipsSuv, philipsБкМл,
  };
}

async function окноSUV() {
  const все = getAllSeries?.() || [];
  const пт = все.filter((с) => String(с.modality || '').toUpperCase() === 'PT');
  if (!пт.length) { showToast('SUV считается по ПЭТ: в исследовании нет серии PT', 'warning', 6000); return; }
  const серия = пт.reduce((а, б) => ((б.numberOfFrames || 0) > (а.numberOfFrames || 0) ? б : а));
  const uid = серия.seriesUID;
  const ids = getSeriesImageIds?.(uid) || [];
  if (!ids.length) { showToast('У серии PT нет кадров', 'warning', 5000); return; }

  showToast('Читаю теги ПЭТ…', 'info', 2000);
  const теги = await пэтТеги(ids[Math.floor(ids.length / 2)]);
  const w = окно(`ПЭТ: SUV — ${серия.seriesDescription || uid.slice(0, 24)}`, { ширина: 640 });

  const форма = document.createElement('div');
  форма.innerHTML = строкаВвода('Вес пациента, кг (0010,1030)', 'suv-вес', Number.isFinite(теги.весКг) ? теги.весКг : '')
    + строкаВвода('Введённая доза, МБк (0018,1074)', 'suv-доза', Number.isFinite(теги.дозаБк) ? (теги.дозаБк / 1e6).toFixed(1) : '')
    + строкаВвода('Полупериод, с (0018,1075)', 'suv-полупериод', Number.isFinite(теги.полупериодС) ? теги.полупериодС : '6588')
    + строкаВвода('Время введения (0018,1072)', 'suv-введение', теги.времяВведения || '')
    + строкаВвода('Время серии (0008,0031)', 'suv-серия', теги.времяСерии || '');
  const итог = document.createElement('div');
  итог.style.cssText = 'margin-top:10px;padding:10px;border:1px solid #2a3546;border-radius:8px;background:#0d1420;';
  const мтв = document.createElement('div');
  мтв.innerHTML = строкаВвода('Порог MTV: доля от SUVmax (0.40) или…', 'suv-порог-доля', '0.40', 'style="width:70px"')
    + строкаВвода('…абсолютный SUV (пусто = по доле)', 'suv-порог-абс', '', 'style="width:70px"');
  w.тело.append(форма, итог, мтв);
  w.тело.insertAdjacentHTML('beforeend', ОГОВОРКА);

  const пересчитать = () => {
    const п = {
      ...теги,
      весКг: Number($('suv-вес')?.value),
      дозаБк: Number($('suv-доза')?.value) * 1e6,
      полупериодС: Number($('suv-полупериод')?.value),
      времяВведения: $('suv-введение')?.value || теги.времяВведения,
      времяСерии: $('suv-серия')?.value || теги.времяСерии,
    };
    const ф = факторSUVbw(п);
    if (!ф.ok) {
      итог.innerHTML = `<b style="color:#f2b8b5;">SUV не считается:</b> ${ф.причина}.`
        + '<div style="color:#9fb3cf;margin-top:6px;">Недостающее можно вписать руками выше — расчёт обновится.</div>';
      return null;
    }
    const д = ф.подробности;
    итог.innerHTML = '<b>Выкладка фактора (QIBA, SUVbw):</b>'
      + строкаЧисла('Единицы пикселя (0054,1001)', д.units || '—')
      + (д.коррекция ? строкаЧисла('Коррекция распада (0054,1102)', д.коррекция) : '')
      + (Number.isFinite(д.дозаМБк) ? строкаЧисла('Доза', `${д.дозаМБк.toFixed(1)} МБк`) : '')
      + (Number.isFinite(д.весКг) ? строкаЧисла('Вес', `${д.весКг} кг`) : '')
      + (д.какСчиталиВремя ? строкаЧисла('Время', д.какСчиталиВремя) : '')
      + (Number.isFinite(д.остатокДозыДоля) ? строкаЧисла('Осталось дозы к съёмке', `${(д.остатокДозыДоля * 100).toFixed(1)}% → ${д.дозаКСъёмкеМБк.toFixed(1)} МБк`) : '')
      + строкаЧисла('Фактор: пиксель → SUVbw', ф.фактор.toExponential(4))
      + (д.формула ? `<div style="color:#9fb3cf;margin-top:4px;">${д.формула}</div>` : '');
    return ф.фактор;
  };

  const кнСчёт = document.createElement('button');
  кнСчёт.textContent = 'SUV по тому и маске';
  кнСчёт.style.cssText = 'background:#1d3a2a;border:1px solid #2f5c43;color:#d7f5e3;border-radius:8px;padding:7px 14px;cursor:pointer;';
  кнСчёт.addEventListener('click', () => {
    const фактор = пересчитать();
    if (фактор === null) return;
    const т = томСерии(uid);
    if (т.ошибка) { showToast('Том ПЭТ: ' + т.ошибка, 'warning', 7000); return; }
    const { маска, слов } = маскаДляТома(т.dims);
    const стат = статистикаSUV(т.скаляры, { фактор, dims: т.dims, spacing: т.spacing, маска });
    if (!стат.ok) { showToast('SUV: ' + стат.причина, 'warning', 7000); return; }
    let html = `<hr style="border-color:#2a3546;"><b>${маска ? 'По маске' : 'По всему тому'}</b> (${слов}):`
      + строкаЧисла('SUVmax', стат.suvMax.toFixed(2))
      + строкаЧисла('SUVmean', стат.suvMean.toFixed(2))
      + строкаЧисла('SUVpeak (сфера 1 см³)', стат.пикЧестен ? стат.suvPeak.toFixed(2) : `${стат.suvPeak.toFixed(2)} (сетка груба — взят SUVmax)`)
      + строкаЧисла('Вокселей в счёте', стат.вокселей.toLocaleString('ru-RU'));
    if (маска) {
      const абс = Number($('suv-порог-абс')?.value);
      const порог = Number.isFinite(абс) && абс > 0
        ? { вид: 'абс', значение: абс }
        : { вид: 'долямакс', значение: Number($('suv-порог-доля')?.value) || 0.40 };
      const м = mtvTlg(т.скаляры, { фактор, dims: т.dims, spacing: т.spacing, маска, порог });
      if (м.ok) {
        html += строкаЧисла(`MTV (SUV ≥ ${м.порогSUV.toFixed(2)})`, `${м.mtvМл.toFixed(2)} мл`)
          + строкаЧисла('TLG', `${м.tlg.toFixed(1)} г`)
          + строкаЧисла('SUVmean объёма', м.suvMeanОбъёма.toFixed(2));
      } else html += `<div style="color:#f2b8b5;">MTV: ${м.причина}</div>`;
    } else {
      html += '<div style="color:#9fb3cf;">MTV/TLG считаются внутри маски: обведите очаг кистью и нажмите ещё раз.</div>';
    }
    итог.insertAdjacentHTML('beforeend', html);
  });
  w.низ.prepend(кнСчёт);
  пересчитать();
  форма.addEventListener('change', пересчитать);
}

// ══════════════════════════════════════════════════════════════
//  2. Перфузия КТ (инсульт)
// ══════════════════════════════════════════════════════════════

async function окноПерфузии() {
  const все = getAllSeries?.() || [];
  const кт = все.filter((с) => String(с.modality || '').toUpperCase() === 'CT');
  if (!кт.length) { showToast('Перфузия считается по динамической КТ: серий CT не нашлось', 'warning', 6000); return; }
  const серия = кт.reduce((а, б) => ((б.numberOfFrames || 0) > (а.numberOfFrames || 0) ? б : а));
  showToast('Собираю динамику (кадры одного среза по времени)…', 'info', 3000);
  const д = await динамикаСерии(серия.seriesUID, { минВремён: 8 });
  if (д.ошибка) { showToast('Перфузия: ' + д.ошибка, 'warning', 8000); return; }

  const w = окно(`Перфузия КТ — ${серия.seriesDescription || серия.seriesUID.slice(0, 24)}`, { ширина: 760 });
  w.тело.insertAdjacentHTML('beforeend',
    `<div style="color:#9fb3cf;margin-bottom:8px;">Кадров по времени: <b>${д.кадры.length}</b>, позиций среза в серии: ${д.срезов}.`
    + (д.обрезано ? ' Серия велика — просмотрены первые 2400 кадров.' : '') + '</div>');

  // Кандидаты в артерию + канвас последнего «яркого» кадра для щелчка.
  const канд = кандидатыАИФ(д.кадры, д.ширина, Math.max(2, Math.floor(д.кадры.length / 8)), 5);
  let пик = 0, iПик = 0;
  д.кадры.forEach((к, i) => { let м = 0; for (let j = 0; j < к.length; j += 7) м = Math.max(м, к[j]); if (м > пик) { пик = м; iПик = i; } });
  const холст = нарисовать(д.кадры[iПик], д.ширина, д.высота, 0, Math.max(120, пик));
  w.тело.appendChild(холст);
  const форма = document.createElement('div');
  форма.innerHTML = строкаВвода('Артерия: x', 'pf-x', канд[0]?.x ?? 0, 'style="width:70px"')
    + строкаВвода('Артерия: y', 'pf-y', канд[0]?.y ?? 0, 'style="width:70px"')
    + `<div style="color:#9fb3cf;margin:4px 0;">Щелчок по кадру выше ставит точку артерии. Автокандидаты: `
    + (канд.map((к, i) => `№${i + 1} (${к.x},${к.y}) подъём ${к.амплитуда.toFixed(0)} HU`).join('; ') || 'не найдены — укажите щелчком') + '.</div>';
  w.тело.appendChild(форма);
  щёлкающийКанвас(холст, д.ширина, д.высота, (x, y) => { $('pf-x').value = x; $('pf-y').value = y; });

  const вывод = document.createElement('div');
  w.тело.appendChild(вывод);
  w.тело.insertAdjacentHTML('beforeend',
    '<div style="margin-top:8px;color:#e8d48b;">Честно о методе: CBF здесь ЗАНИЖЕН самой деконволюцией '
    + '(промерено тестом: 60–70% истинного при коротком MTT) — сравнивайте стороны между собой, порогам '
    + 'абсолютных чисел без калибровки не верьте. Tmax передаёт РАЗНОСТИ задержек точно.</div>' + ОГОВОРКА);

  const кн = document.createElement('button');
  кн.textContent = 'Считать карты';
  кн.style.cssText = 'background:#1d3a2a;border:1px solid #2f5c43;color:#d7f5e3;border-radius:8px;padding:7px 14px;cursor:pointer;';
  кн.addEventListener('click', () => {
    const x = Number($('pf-x')?.value), y = Number($('pf-y')?.value);
    showToast('Деконволюция…', 'info', 2000);
    setTimeout(() => {
      const к = картыПерфузии(д.кадры, д.времена, { x, y }, { ширина: д.ширина });
      if (!к.ok) { showToast('Перфузия: ' + к.причина, 'warning', 8000); return; }
      вывод.innerHTML = `<div style="color:#9fb3cf;margin:6px 0;">Посчитано ${к.посчитано.toLocaleString('ru-RU')} вокс.,`
        + ` шаг ${к.dt.toFixed(1)} с, фоновых кадров ${к.базлайнКадров}${к.неровныйШаг ? ', шаг по времени неровный' : ''}.</div>`;
      const карты = [
        ['CBF, мл/100г/мин (метод занижает)', к.cbf, 0, 80],
        ['CBV, мл/100г', к.cbv, 0, 6],
        ['MTT, с', к.mtt, 0, 14],
        ['Tmax, с (порог пенумбры в литературе: > 6 с)', к.tmax, 0, 12],
      ];
      const сетка = document.createElement('div');
      сетка.style.cssText = 'display:grid;grid-template-columns:1fr 1fr;gap:10px;margin-top:6px;';
      for (const [имя, карта, низ, верх] of карты) {
        const ячейка = document.createElement('div');
        ячейка.insertAdjacentHTML('beforeend', `<div style="color:#9fb3cf;margin-bottom:3px;">${имя} · шкала ${низ}–${верх}</div>`);
        ячейка.appendChild(нарисовать(карта, д.ширина, д.высота, низ, верх));
        сетка.appendChild(ячейка);
      }
      вывод.appendChild(сетка);
    }, 30);
  });
  w.низ.prepend(кн);
}

// ══════════════════════════════════════════════════════════════
//  3. ДКУ-МРТ: Ktrans
// ══════════════════════════════════════════════════════════════

async function окноКтранс() {
  const все = getAllSeries?.() || [];
  const мр = все.filter((с) => String(с.modality || '').toUpperCase() === 'MR');
  if (!мр.length) { showToast('Ktrans считается по динамической МР: серий MR не нашлось', 'warning', 6000); return; }
  const серия = мр.reduce((а, б) => ((б.numberOfFrames || 0) > (а.numberOfFrames || 0) ? б : а));
  showToast('Собираю динамику ДКУ…', 'info', 3000);
  const д = await динамикаСерии(серия.seriesUID, { минВремён: 6 });
  if (д.ошибка) { showToast('ДКУ: ' + д.ошибка, 'warning', 8000); return; }

  let теги = {};
  try { теги = await getDICOMTags((getSeriesImageIds?.(серия.seriesUID) || [])[0]); } catch (e) { /* поля заполнит врач */ }
  const trМс = Number(теги?.['x00180080']?.value);
  const fa = Number(теги?.['x00181314']?.value);

  const w = окно(`ДКУ-МРТ: Ktrans — ${серия.seriesDescription || серия.seriesUID.slice(0, 24)}`, { ширина: 760 });
  w.тело.insertAdjacentHTML('beforeend',
    `<div style="color:#9fb3cf;margin-bottom:8px;">Кадров по времени: <b>${д.кадры.length}</b>. Сигнал переводится в`
    + ' концентрацию по SPGR; T1 ткани до контраста — ввод (по умолчанию 1400 мс), АИФ — популяционная Паркера'
    + ' или точка на артерии щелчком по кадру.</div>');
  let пик = 0, iПик = д.кадры.length - 1;
  д.кадры.forEach((к, i) => { let м = 0; for (let j = 0; j < к.length; j += 7) м = Math.max(м, к[j]); if (м > пик) { пик = м; iПик = i; } });
  const холст = нарисовать(д.кадры[iПик], д.ширина, д.высота, 0, пик || 1);
  w.тело.appendChild(холст);
  const форма = document.createElement('div');
  форма.innerHTML = строкаВвода('TR, мс (0018,0080)', 'kt-tr', Number.isFinite(trМс) ? trМс : '', 'style="width:90px"')
    + строкаВвода('Угол отклонения, ° (0018,1314)', 'kt-fa', Number.isFinite(fa) ? fa : '', 'style="width:90px"')
    + строкаВвода('T1 ткани до контраста, мс', 'kt-t10', '1400', 'style="width:90px"')
    + строкаВвода('Релаксивность r1, 1/(ммоль·с)', 'kt-r1', '3.6', 'style="width:90px"')
    + строкаВвода('Фоновых кадров (до болюса)', 'kt-фон', String(Math.max(2, Math.floor(д.кадры.length / 10))), 'style="width:90px"')
    + строкаВвода('АИФ: пусто = Паркер; либо точка x,y', 'kt-аиф', '', 'placeholder="например 128,90" style="width:130px"');
  w.тело.appendChild(форма);
  щёлкающийКанвас(холст, д.ширина, д.высота, (x, y) => { $('kt-аиф').value = `${x},${y}`; });

  const вывод = document.createElement('div');
  w.тело.appendChild(вывод);
  w.тело.insertAdjacentHTML('beforeend',
    '<div style="margin-top:8px;color:#e8d48b;">Главный счёт — Тофтс по Мурасэ (линейный, проверен тестом с точностью 2%'
    + ' на чистых данных). Патлак приложен как быстрая прикидка и честен только при слабом вымывании.</div>' + ОГОВОРКА);

  const кн = document.createElement('button');
  кн.textContent = 'Считать Ktrans';
  кн.style.cssText = 'background:#1d3a2a;border:1px solid #2f5c43;color:#d7f5e3;border-radius:8px;padding:7px 14px;cursor:pointer;';
  кн.addEventListener('click', () => {
    const trС = Number($('kt-tr')?.value) / 1000;
    const flipГрад = Number($('kt-fa')?.value);
    const t10С = Number($('kt-t10')?.value) / 1000;
    const r1 = Number($('kt-r1')?.value) || 3.6;
    const фонК = Math.max(2, Number($('kt-фон')?.value) || 2);
    if (!(trС > 0) || !(flipГрад > 0)) { showToast('Ktrans: без TR и угла отклонения SPGR не решается — впишите их', 'warning', 8000); return; }
    showToast('Перевод сигнала в концентрацию и подгонка…', 'info', 2500);
    setTimeout(() => {
      const T = д.кадры.length, всего = д.кадры[0].length;
      // Сигнал → концентрация: S0 = среднее фоновых кадров, повоксельно.
      const s0 = new Float64Array(всего);
      for (let t = 0; t < фонК; t++) for (let i = 0; i < всего; i++) s0[i] += д.кадры[t][i];
      for (let i = 0; i < всего; i++) s0[i] /= фонК;
      const конц = [];
      const пСпгр = { t10С, trС, flipГрад, r1 };
      for (let t = 0; t < T; t++) {
        const к = new Float32Array(всего);
        for (let i = 0; i < всего; i++) {
          if (!(s0[i] > 1)) continue;   // воздух/фон
          const c = концентрацияИзSPGR(д.кадры[t][i], s0[i], пСпгр);
          к[i] = Number.isFinite(c) && c > 0 ? c : 0;
        }
        конц.push(к);
      }
      // АИФ.
      let cp;
      const точка = String($('kt-аиф')?.value || '').match(/^\s*(\d+)\s*,\s*(\d+)\s*$/);
      if (точка) {
        const i = Number(точка[1]) + Number(точка[2]) * д.ширина;
        cp = Float64Array.from({ length: T }, (_, t) => конц[t][i]);
        if (!cp.some((v) => v > 0.1)) { showToast('В указанной точке контраст не пришёл — это не артерия', 'warning', 8000); return; }
      } else {
        // Паркер: начало болюса — где средняя концентрация тронулась с места.
        const средняя = new Float64Array(T);
        for (let t = 0; t < T; t++) { let с = 0; for (let i = 0; i < всего; i += 11) с += конц[t][i]; средняя[t] = с; }
        const макс = Math.max(...средняя);
        let старт = 0;
        while (старт < T - 1 && средняя[старт] < макс * 0.1) старт++;
        const t0 = д.времена[Math.max(0, старт - 1)];
        cp = Float64Array.from({ length: T }, (_, t) => паркерAIF(д.времена[t] - t0));
      }
      const карты = картыКтранс(конц, cp, д.времена, { метод: 'мурасэ', расширенный: true });
      if (!карты.ok) { showToast('Ktrans: ' + карты.причина, 'warning', 8000); return; }
      вывод.innerHTML = `<div style="color:#9fb3cf;margin:6px 0;">Посчитано ${карты.посчитано.toLocaleString('ru-RU')} вокс.,`
        + ` отказов подгонки ${карты.отказов}. АИФ: ${точка ? 'точка (' + точка[1] + ',' + точка[2] + ')' : 'Паркер (популяционная)'}.</div>`;
      const сетка = document.createElement('div');
      сетка.style.cssText = 'display:grid;grid-template-columns:1fr 1fr;gap:10px;margin-top:6px;';
      for (const [имя, карта, низ, верх] of [
        ['Ktrans, 1/мин', карты.ktrans, 0, 0.4],
        ['ve (доля внеклеточного)', карты.ve, 0, 1],
        ['kep, 1/мин', карты.kep, 0, 1.2],
        ['vp (доля плазмы)', карты.vp, 0, 0.1],
      ]) {
        const ячейка = document.createElement('div');
        ячейка.insertAdjacentHTML('beforeend', `<div style="color:#9fb3cf;margin-bottom:3px;">${имя} · шкала ${низ}–${верх}</div>`);
        ячейка.appendChild(нарисовать(карта, д.ширина, д.высота, низ, верх));
        сетка.appendChild(ячейка);
      }
      вывод.appendChild(сетка);
    }, 30);
  });
  w.низ.prepend(кн);
}

// ══════════════════════════════════════════════════════════════
//  ПРИВЯЗКА
// ══════════════════════════════════════════════════════════════

export function initPetPerfusionTools() {
  $('btn-pet-suv')?.addEventListener('click', () => { окноSUV().catch((e) => { log.warn('[SUV]', e); showToast('SUV: ' + e.message, 'error', 8000); }); });
  $('btn-perfusion-ct')?.addEventListener('click', () => { окноПерфузии().catch((e) => { log.warn('[Перфузия]', e); showToast('Перфузия: ' + e.message, 'error', 8000); }); });
  $('btn-dce-ktrans')?.addEventListener('click', () => { окноКтранс().catch((e) => { log.warn('[Ktrans]', e); showToast('Ktrans: ' + e.message, 'error', 8000); }); });
  log.debug('[PetPerfusion] Готово: SUV, перфузия КТ, ДКУ Ktrans');
}
